import { useEffect, useRef, useState } from 'react'
import { MapPin, Navigation, Loader } from 'lucide-react'

interface HereMapProps {
  center?: { lat: number; lng: number }
  zoom?: number
  width?: string
  height?: string
  markers?: Array<{
    lat: number
    lng: number
    title?: string
    info?: string
    icon?: string
  }>
  onMapClick?: (event: { lat: number; lng: number }) => void
  showCurrentLocation?: boolean
  className?: string
}

declare global {
  interface Window {
    H: any
  }
}

const HereMap = ({
  center = { lat: 52.520008, lng: 13.404954 }, // Default to Berlin
  zoom = 10,
  width = '100%',
  height = '400px',
  markers = [],
  onMapClick,
  showCurrentLocation = true,
  className = ''
}: HereMapProps) => {
  const mapRef = useRef<HTMLDivElement>(null)
  const map = useRef<any>(null)
  const platform = useRef<any>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [currentLocation, setCurrentLocation] = useState<{ lat: number; lng: number } | null>(null)
  const [error, setError] = useState<string | null>(null)

  // Initialize HERE Maps
  useEffect(() => {
    if (!mapRef.current) {
      return
    }

    // Wait for HERE Maps API to load
    const checkHereAPI = (attempts = 0) => {
      const maxAttempts = 50 // 5 seconds timeout
      
      if (window.H && window.H.service && window.H.service.Platform && 
          window.H.Map && window.H.mapevents && window.H.ui) {
        // Add small delay to ensure WebGL context and DOM are ready
        setTimeout(initializeMap, 500)
      } else if (attempts < maxAttempts) {
        // Retry after 100ms if API not ready
        setTimeout(() => checkHereAPI(attempts + 1), 100)
      } else {
        setError('HERE Maps API failed to load')
        setIsLoading(false)
      }
    }

    const initializeMap = () => {
      const apiKey = import.meta.env.VITE_HERE_API_KEY
      const appId = import.meta.env.VITE_HERE_APP_ID
      
      if (!apiKey) {
        setError('HERE Maps API key not configured')
        setIsLoading(false)
        return
      }

      try {
        console.log('Initializing HERE Platform with:', { 
          apikey: apiKey ? 'present' : 'missing',
          appId: appId ? 'present' : 'missing'
        })
        
        // Try both authentication methods - first with both app_id and apikey (legacy)
        // If that fails, we'll try with just apikey
        let platformConfig: any = {
          'apikey': apiKey
        }
        
        // Include app_id if available (some older HERE implementations might need this)
        if (appId) {
          platformConfig['app_id'] = appId
        }
        
        platform.current = new window.H.service.Platform(platformConfig)
        
        console.log('Platform created:', platform.current)

        // Get default map layers (as shown in HERE documentation)
        let defaultLayers
        try {
          defaultLayers = platform.current.createDefaultLayers()
          console.log('DefaultLayers object:', defaultLayers)
          console.log('DefaultLayers keys:', defaultLayers ? Object.keys(defaultLayers) : 'undefined')
        } catch (layersError) {
          console.error('Error creating default layers:', layersError)
          throw new Error(`Failed to create default layers: ${layersError instanceof Error ? layersError.message : String(layersError)}`)
        }
        
        // Explore the layers structure to understand what's available
        console.log('Exploring layers structure:')
        console.log('defaultLayers.vector:', defaultLayers.vector)
        console.log('defaultLayers.raster:', defaultLayers.raster)
        
        if (defaultLayers.vector) {
          console.log('Vector layer keys:', Object.keys(defaultLayers.vector))
          if (defaultLayers.vector.normal) {
            console.log('Vector.normal keys:', Object.keys(defaultLayers.vector.normal))
          }
        }
        
        if (defaultLayers.raster) {
          console.log('Raster layer keys:', Object.keys(defaultLayers.raster))
          if (defaultLayers.raster.normal) {
            console.log('Raster.normal keys:', Object.keys(defaultLayers.raster.normal))
          }
        }
        
        // Try to get map layer using the most common patterns
        let mapLayer = null
        
        // Method 1: Try vector.normal.map (most common in documentation)
        try {
          if (defaultLayers.vector && defaultLayers.vector.normal && defaultLayers.vector.normal.map) {
            mapLayer = defaultLayers.vector.normal.map
            console.log('Using vector.normal.map layer')
          }
        } catch (e) {
          console.log('Failed to access vector.normal.map:', e)
        }
        
        // Method 2: Try raster.normal.map as fallback
        if (!mapLayer) {
          try {
            if (defaultLayers.raster && defaultLayers.raster.normal && defaultLayers.raster.normal.map) {
              mapLayer = defaultLayers.raster.normal.map
              console.log('Using raster.normal.map layer')
            }
          } catch (e) {
            console.log('Failed to access raster.normal.map:', e)
          }
        }

        // Method 3: Try direct access to any available layer type
        if (!mapLayer) {
          try {
            const layerTypes = ['vector', 'raster']
            for (const layerType of layerTypes) {
              const layer = defaultLayers[layerType]
              if (layer && typeof layer === 'object') {
                // Look for any properties that might be map layers
                for (const [key, value] of Object.entries(layer)) {
                  if (value && typeof value === 'object' && 'map' in value) {
                    mapLayer = value.map
                    console.log(`Using ${layerType}.${key}.map layer`)
                    break
                  }
                }
                if (mapLayer) break
              }
            }
          } catch (e) {
            console.log('Failed to find any map layer:', e)
          }
        }

        if (!mapLayer) {
          console.error('Complete layers structure:', JSON.stringify(defaultLayers, null, 2))
          throw new Error('No suitable map layer found in available layers')
        }
        
        console.log('Selected map layer:', mapLayer)

        // Verify map container is ready
        if (!mapRef.current) {
          throw new Error('Map container element not available')
        }
        
        console.log('Map container ready:', mapRef.current)
        console.log('Map initialization parameters:', {
          container: mapRef.current,
          layer: mapLayer,
          zoom: zoom,
          center: center
        })

        // Initialize map with error handling
        try {
          map.current = new window.H.Map(
            mapRef.current,
            mapLayer,
            {
              zoom: zoom,
              center: { lat: center.lat, lng: center.lng }
            }
          )
          console.log('H.Map created successfully:', map.current)
        } catch (mapError) {
          console.error('Error creating H.Map:', mapError)
          // Try with a different approach - use raster layer as fallback
          if (defaultLayers.raster && defaultLayers.raster.normal && defaultLayers.raster.normal.map) {
            console.log('Attempting fallback to raster map...')
            map.current = new window.H.Map(
              mapRef.current,
              defaultLayers.raster.normal.map,
              {
                zoom: zoom,
                center: { lat: center.lat, lng: center.lng }
              }
            )
            console.log('Fallback H.Map created successfully')
          } else {
            throw mapError
          }
        }

        // Add behavior control (pan and zoom)
        let behavior: any, ui: any
        try {
          behavior = new window.H.mapevents.Behavior(new window.H.mapevents.MapEvents(map.current))
          console.log('Map behavior created successfully')
        } catch (behaviorError) {
          console.error('Error creating map behavior:', behaviorError)
          // Continue without behavior - map will still display but won't be interactive
        }
        
        // Add UI controls
        try {
          ui = window.H.ui.UI.createDefault(map.current)
          console.log('Map UI controls created successfully')
        } catch (uiError) {
          console.error('Error creating UI controls:', uiError)
          // Continue without UI controls - map will still display but without zoom/pan buttons
        }
        
        // Ensure variables are considered used
        console.log('Map initialized with behavior:', !!behavior, 'and ui:', !!ui)

        // Add click event listener
        if (onMapClick) {
          try {
            map.current.addEventListener('tap', (evt: any) => {
              const coord = map.current.screenToGeo(
                evt.currentPointer.viewportX,
                evt.currentPointer.viewportY
              )
              onMapClick({ lat: coord.lat, lng: coord.lng })
            })
            console.log('Map click event listener added successfully')
          } catch (clickError) {
            console.error('Error adding click event listener:', clickError)
          }
        }

        console.log('HERE Map initialization completed successfully!')
        setIsLoading(false)
      } catch (err) {
        console.error('Error initializing HERE Map:', err)
        setError(`Failed to initialize map: ${err instanceof Error ? err.message : 'Unknown error'}`)
        setIsLoading(false)
      }
    }

    // Start checking for HERE API availability
    checkHereAPI()

    // Cleanup
    return () => {
      if (map.current) {
        map.current.dispose()
      }
    }
  }, [center.lat, center.lng, zoom, onMapClick])

  // Update map center when center prop changes
  useEffect(() => {
    if (map.current && center) {
      map.current.setCenter({ lat: center.lat, lng: center.lng })
      console.log('Map center updated to:', center)
    }
  }, [center.lat, center.lng])

  // Add markers
  useEffect(() => {
    if (!map.current || !window.H) return

    // Clear existing markers
    map.current.removeObjects(map.current.getObjects())

    // Add new markers
    markers.forEach((marker) => {
      try {
        const markerIcon = new window.H.map.Icon(
          marker.icon || `<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M12 2C8.13 2 5 5.13 5 9C5 14.25 12 22 12 22S19 14.25 19 9C19 5.13 15.87 2 12 2ZM12 11.5C10.62 11.5 9.5 10.38 9.5 9C9.5 7.62 10.62 6.5 12 6.5C13.38 6.5 14.5 7.62 14.5 9C14.5 10.38 13.38 11.5 12 11.5Z" fill="#3B82F6"/>
          </svg>`,
          { size: { w: 24, h: 24 } }
        )

        const mapMarker = new window.H.map.Marker(
          { lat: marker.lat, lng: marker.lng },
          { icon: markerIcon }
        )

        // Add info bubble if title or info provided
        if (marker.title || marker.info) {
          mapMarker.addEventListener('tap', () => {
            const bubble = new window.H.ui.InfoBubble(
              marker.title || 'Location',
              {
                content: marker.info || ''
              }
            )
            bubble.setPosition({ lat: marker.lat, lng: marker.lng })
            map.current.getUI().addBubble(bubble)
          })
        }

        map.current.addObject(mapMarker)
      } catch (err) {
        console.error('Error adding marker:', err)
      }
    })
  }, [markers])

  // Get current location
  useEffect(() => {
    if (!showCurrentLocation) return

    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const location = {
            lat: position.coords.latitude,
            lng: position.coords.longitude
          }
          setCurrentLocation(location)
        },
        (error) => {
          console.warn('Could not get current location:', error)
        },
        { timeout: 10000, enableHighAccuracy: true }
      )
    }
  }, [showCurrentLocation])

  // Add current location marker when location is available
  useEffect(() => {
    if (!map.current || !window.H || !currentLocation || !showCurrentLocation) return

    // Add current location marker
    const currentLocationIcon = new window.H.map.Icon(
      `<svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
        <circle cx="10" cy="10" r="8" fill="#10B981" stroke="#ffffff" stroke-width="2"/>
        <circle cx="10" cy="10" r="3" fill="#ffffff"/>
      </svg>`,
      { size: { w: 20, h: 20 } }
    )

    const currentLocationMarker = new window.H.map.Marker(
      currentLocation,
      { icon: currentLocationIcon }
    )

    // Add marker to map
    map.current.addObject(currentLocationMarker)
    console.log('Current location marker added:', currentLocation)

    // Cleanup function to remove marker when effect re-runs
    return () => {
      if (map.current && currentLocationMarker) {
        map.current.removeObject(currentLocationMarker)
      }
    }
  }, [currentLocation, showCurrentLocation])

  const centerOnCurrentLocation = () => {
    if (currentLocation && map.current) {
      map.current.setCenter(currentLocation)
      map.current.setZoom(15)
    } else if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const location = {
            lat: position.coords.latitude,
            lng: position.coords.longitude
          }
          if (map.current) {
            map.current.setCenter(location)
            map.current.setZoom(15)
          }
        },
        (error) => {
          console.error('Error getting location:', error)
        }
      )
    }
  }

  if (error) {
    return (
      <div 
        className={`bg-gray-100 border border-gray-300 rounded-lg flex items-center justify-center ${className}`}
        style={{ width, height }}
      >
        <div className="text-center text-gray-500">
          <MapPin className="h-12 w-12 mx-auto mb-2 text-gray-400" />
          <p className="text-sm">{error}</p>
        </div>
      </div>
    )
  }

  return (
    <div className={`relative ${className}`} style={{ width, height }}>
      {isLoading && (
        <div className="absolute inset-0 bg-gray-100 flex items-center justify-center z-10 rounded-lg">
          <div className="text-center">
            <Loader className="h-8 w-8 animate-spin text-blue-600 mx-auto" />
            <p className="text-sm text-gray-600 mt-2">Loading map...</p>
          </div>
        </div>
      )}
      
      <div 
        ref={mapRef} 
        className="w-full h-full rounded-lg border border-gray-300"
        style={{ width, height }}
      />
      
      {showCurrentLocation && (
        <button
          onClick={centerOnCurrentLocation}
          className="absolute bottom-4 right-4 bg-white border border-gray-300 rounded-full p-2 shadow-lg hover:bg-gray-50 transition-colors"
          title="Center on my location"
        >
          <Navigation className="h-5 w-5 text-gray-600" />
        </button>
      )}
    </div>
  )
}

export default HereMap